import { not } from 'ramda';
import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import type { AnyAction } from 'redux';
import { combineReducers } from 'redux';
import styled from 'styled-components';
import { redirectToLogin } from '@peloton/auth';
import type { Locale } from '@peloton/internationalize/models/locale';
import { getIsBreakpointLessThan } from '@peloton/responsive';
import { media } from '@peloton/styles';
import { gray1, slate1 } from '@engage/colors';
import { dispatchify } from '@engage/dispatchify';
import { useReduxAction, useReduxState } from '@engage/redux';
import {
  updateClassLanguagePreferences,
  getClassLanguagePreferences,
} from '@engage/settings';
import type { ClassLanguage } from '@engage/settings';
import type { Source } from '@engage/settings/analytics';
import { spaces } from '@engage/styles';
import { header4Large, header8Large, header6Small } from '@engage/typography';
import { Button1Large } from '@members/buttons';
import { useSettings, SettingsCopy } from '@members/copy';
import { ModalLabel } from '@members/copy/models.generated/ModalLabel';
import { Settings } from '@members/copy/models.generated/Settings';
import CustomizableToast, { StyledMessageAndIcon } from '@members/notifications/Toast';
import { LanguageOption } from './LanguageOption';
import {
  displayAllClasses as displayAllClassesAction,
  displayOnlySubtitles as displayOnlySubtitlesAction,
  toggleIndividualSubtitle as toggleIndividualSubtitleAction,
  enableLanguage as enableLanguageAction,
  disableLanguage as disableLanguageAction,
  showUserError as showUserErrorAction,
  showApiError as showApiErrorAction,
  toastClosed as toastClosedAction,
  replaceState as replaceStateAction,
  isValid,
} from './localReducers';
import { classLanguagesReducer } from './localReducers/classLanguagesReducer';
import { toastReducer } from './localReducers/toastReducer';

export const CLASS_LANGUAGE_MODAL_NAME = 'classLanguages';

export const modalProps = {
  ariaLabelId: ModalLabel.ClassLanguages,
  showMobileCloseButton: false,
  style: { content: { maxWidth: 580 } },
};

const reducer = combineReducers({
  language: classLanguagesReducer,
  toast: toastReducer,
});

export const ClassLanguageModal: React.FC<
  React.PropsWithChildren<Pick<FormProps, 'closeModal' | 'onSuccessfulSave' | 'source'>>
> = ({ closeModal, onSuccessfulSave, source }) => {
  const classLanguagePreferences = useReduxState(getClassLanguagePreferences);
  const [state, dispatch] = React.useReducer(reducer, {
    language: classLanguagePreferences,
    toast: { showing: false, messageId: '' },
  });

  const closeToast = dispatchify(toastClosedAction, dispatch);
  const replaceState = dispatchify(replaceStateAction, dispatch);

  React.useEffect(() => {
    replaceState(classLanguagePreferences);
  }, [classLanguagePreferences.length]);

  const toastMessage = useSettings(Settings[state.toast.messageId]) ?? '';
  const isBreakpointLessThanTablet = useReduxState(getIsBreakpointLessThan, 'tablet');

  return (
    <Container data-test-id="classLanguagesModal">
      <StyledToast afterClose={closeToast} hide={not(state.toast.showing)}>
        {toastMessage}
      </StyledToast>
      {isBreakpointLessThanTablet ? (
        <MobileCancelButton
          onClick={closeModal}
          data-test-id="mobileClassLanguageCancelButton"
        >
          <SettingsCopy id={Settings.Cancel} />
        </MobileCancelButton>
      ) : null}
      <Title>
        <SettingsCopy id={Settings.LanguagesModal} />
      </Title>
      <Description>
        <SettingsCopy id={Settings.LanguagesModalDescription} />
      </Description>

      {state.language.length > 0 ? (
        <LanguageSelectionForm
          classLanguagePreferences={state.language}
          previousClassLanguagePreferences={classLanguagePreferences}
          dispatch={dispatch}
          closeModal={closeModal}
          onSuccessfulSave={onSuccessfulSave}
          source={source}
        />
      ) : null}
    </Container>
  );
};

type FormProps = {
  classLanguagePreferences: ClassLanguage[];
  previousClassLanguagePreferences: ClassLanguage[];
  dispatch: React.Dispatch<AnyAction>;
  closeModal: () => void;
  onSuccessfulSave: () => void;
  source: Source;
};

const LanguageSelectionForm: React.FC<React.PropsWithChildren<FormProps>> = ({
  classLanguagePreferences,
  previousClassLanguagePreferences,
  dispatch,
  closeModal,
  onSuccessfulSave,
  source,
}) => {
  const enableLanguage = dispatchify(enableLanguageAction, dispatch);
  const disableLanguage = dispatchify(disableLanguageAction, dispatch);
  const displayOnlySubtitles = dispatchify(displayOnlySubtitlesAction, dispatch);
  const displayAllClasses = dispatchify(displayAllClassesAction, dispatch);
  const toggleSubtitle = dispatchify(toggleIndividualSubtitleAction, dispatch);
  const updateClassLanguage = useReduxAction(updateClassLanguagePreferences);
  const showUserErrorToast = dispatchify(showUserErrorAction, dispatch);
  const showApiErrorToast = dispatchify(showApiErrorAction, dispatch);
  const location = useLocation();
  const history = useHistory();

  const handleSubmit = (languagePreferences: ClassLanguage[]) => {
    let numDisplayLanguage = 0;
    for (let i = 0; i < languagePreferences.length; i++) {
      if (languagePreferences[i].displayAllClasses) {
        numDisplayLanguage++;
      }
    }
    if (numDisplayLanguage < 2) {
      const queryParams = new URLSearchParams(location.search);
      if (queryParams.has('class_languages')) {
        queryParams.delete('class_languages');
        history.replace({
          search: queryParams.toString(),
        });
      }
    }
    if (isValid(languagePreferences)) {
      updateClassLanguage(
        { classLanguagePreferences: languagePreferences },
        { classLanguagePreferences: previousClassLanguagePreferences },
        {
          redirectToLoginPage: redirectToLogin,
          displaySuccessMessage: onSuccessfulSave,
          displayErrorMessage: showApiErrorToast,
        },
        source,
      );
    } else {
      showUserErrorToast();
    }
  };

  return (
    <FormContainer>
      {classLanguagePreferences.map((language: ClassLanguage) => (
        <LanguageOption
          key={language.locale}
          language={language}
          displayOnlySubtitles={() => displayOnlySubtitles(language.locale)}
          displayAllClasses={() => displayAllClasses(language.locale)}
          toggleSubtitle={(subtitleLocale: Locale) =>
            toggleSubtitle({ languageLocale: language.locale, subtitleLocale })
          }
          enableLanguage={() => enableLanguage(language.locale)}
          disableLanguage={() => disableLanguage(language.locale)}
        />
      ))}
      <ButtonsContainer>
        <CancelButton data-test-id="classLanguageCancelButton" onClick={closeModal}>
          <SettingsCopy id={Settings.Cancel} />
        </CancelButton>
        <SaveButton
          data-test-id="classLanguageSaveButton"
          onClick={() => handleSubmit(classLanguagePreferences)}
        >
          <SettingsCopy id={Settings.SaveChanges} />
        </SaveButton>
      </ButtonsContainer>
    </FormContainer>
  );
};

const StyledToast = styled(CustomizableToast)`
  width: 100%;
  top: 0;
  position: absolute;

  ${media.tablet`
    width: 460px;
    top: ${spaces.large}px;
  `}

  ${StyledMessageAndIcon} {
    padding: ${spaces.small}px 0;

    span {
      min-height: ${spaces.xLarge}px;
    }
  }
`;

const Container = styled.div`
  overflow: hidden;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: ${gray1};
  padding: ${spaces.huge}px ${spaces.small}px ${spaces.xxLarge}px;

  ${media.tablet`
    padding: ${spaces.huge}px ${spaces.large}px ${spaces.xxLarge}px;
  `}
`;

const Title = styled.span`
  ${header4Large}
  margin-bottom: ${spaces.small}px;
`;

const Description = styled.span`
  ${header8Large}
  margin-bottom: ${spaces.xxLarge}px;
  text-align: center;
`;

const ButtonsContainer = styled.div`
  margin-top: ${spaces.medium}px;

  ${media.tablet`
    margin-top: ${spaces.xHuge}px;
    display: flex;
  `}
`;

const SaveButton = styled(Button1Large)`
  ${media.tablet`
    margin-left: ${spaces.large}px;
  `}
`;

const CancelButton = styled(Button1Large)`
  display: none;

  ${media.tablet`
    display: block;
    color: ${slate1};
    background-color: ${gray1};
    border: 1px solid ${slate1};
    box-shadow: none;
  `}
`;

const FormContainer = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
`;

const MobileCancelButton = styled.button`
  ${header6Small}
  color: ${slate1};
  position: absolute;
  top: ${spaces.small}px;
  left: ${spaces.small}px;
`;
